/**
 * Points API – all points-related API calls.
 * GET points/overview?wallet=<address> returns overview data.
 */

export type PointsOverviewUserProfile = {
  wallet?: string
  isActivated?: boolean
  isQualified?: boolean
  code?: string | null
  activationTxHash?: string | null
  activatedAt?: string | null
  id?: number
  createdAt?: string
  updatedAt?: string
  [key: string]: unknown
}

export type PointsOverviewUser = {
  wallet?: string
  totalPointsE18?: string
  periodUserPointsE18?: string
  periodTotalPointsE18?: string
  periodToonWei?: string
  estimatedToonWei?: string
  completedTaskCount?: number
  completedTasks?: unknown[]
  profile?: PointsOverviewUserProfile | null
  [key: string]: unknown
}

export type GetPointsOverviewResponse = {
  data?: {
    totalPlatformPointsE18?: string
    currentPeriod?: {
      period?: string
      startDate?: string
      endDate?: string
    }
    user?: PointsOverviewUser | null
    [key: string]: unknown
  } | null
  success?: boolean
  [key: string]: unknown
}

export type PointsOverviewData = NonNullable<GetPointsOverviewResponse['data']>

export type DailyTask = {
  id: number
  code: string
  name: string
  description: string
  type: string
  target: {
    count?: number
    amount?: string
    [key: string]: unknown
  }
  pointsRewardE18: string
  isActive: boolean
  sortOrder: number
  [key: string]: unknown
}

export type DailyTaskListItem = {
  task: DailyTask
  progressValue: string
  targetValue: string
  isCompleted: boolean
  [key: string]: unknown
}

export type PointsByTypeData = {
  wallet?: string
  period?: string
  totalPointsE18?: string
  taskList?: DailyTaskListItem[]
  byType?: {
    daily_checkin?: string
    add_lp?: string
    trade?: string
    referral?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export type GetPointsByTypeResponse = {
  data?: PointsByTypeData | null
  success?: boolean
  [key: string]: unknown
}

export type PointsPeriodHistoryItem = {
  period: string
  startDate?: string
  endDate?: string
  totalPointsE18: string
  byType?: {
    daily_checkin?: string
    add_lp?: string
    trade?: string
    referral?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export type GetPointsPeriodHistoryResponse = {
  data?: {
    history?: PointsPeriodHistoryItem[]
  } | null
  success?: boolean
  [key: string]: unknown
}

export type CheckinHistoryItem = {
  dateKey: string
  pointsE18: string
  createdAt: string
}

export type CheckinHistoryCurrentPeriod = {
  period: string
  startDate: string
  endDate: string
}

export type CheckinHistoryData = {
  checkinHistory: CheckinHistoryItem[]
  serverTime?: string
  currentPeriod?: CheckinHistoryCurrentPeriod
  periodTotalPointsE18?: string
  periodUserEarnedPointsE18?: string
  nextCheckinPointsE18?: string
  longestStreakThisPeriod?: number
  [key: string]: unknown
}

export type GetCheckinHistoryResponse = {
  data?: CheckinHistoryData | null
  success?: boolean
  [key: string]: unknown
}

const POINTS_API_BASE = process.env.REACT_APP_API_BASE_URL || ''
const POINTS_OVERVIEW_PATH = '/points/overview'
const POINTS_BY_TYPE_PATH = '/points/by-type'
const POINTS_PERIOD_HISTORY_PATH = '/points/period-history'
const POINTS_CHECKIN_HISTORY_PATH = '/points/checkin-history'
const POINTS_CHECKIN_PATH = '/points/checkin'

const joinUrl = (base: string, path: string) => {
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${normalizedBase}${normalizedPath}`
}

const getPointsOverviewEndpoint = (wallet: string) =>
  `${joinUrl(POINTS_API_BASE, POINTS_OVERVIEW_PATH)}?wallet=${encodeURIComponent(wallet)}`

const getPointsByTypeEndpoint = (wallet: string) =>
  `${joinUrl(POINTS_API_BASE, POINTS_BY_TYPE_PATH)}?wallet=${encodeURIComponent(wallet)}`

const getPointsPeriodHistoryEndpoint = (wallet: string) =>
  `${joinUrl(POINTS_API_BASE, POINTS_PERIOD_HISTORY_PATH)}?wallet=${encodeURIComponent(wallet)}`

const getCheckinHistoryEndpoint = (wallet: string, period: string) =>
  `${joinUrl(POINTS_API_BASE, POINTS_CHECKIN_HISTORY_PATH)}?wallet=${encodeURIComponent(wallet)}&period=${encodeURIComponent(period)}`

/**
 * GET points/overview?wallet=<wallet>
 * Returns { data: { totalPlatformPointsE18, currentPeriod: { period, startDate, endDate }, user: { wallet, totalPointsE18, periodUserPointsE18, periodTotalPointsE18, periodToonWei, estimatedToonWei, completedTaskCount, completedTasks, profile } }, success }.
 */
export const fetchPointsOverview = async (wallet: string): Promise<GetPointsOverviewResponse> => {
  if (!POINTS_API_BASE) {
    throw new Error('Points API is not configured.')
  }

  const response = await fetch(getPointsOverviewEndpoint(wallet), {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return (await response.json()) as GetPointsOverviewResponse
}

/**
 * GET points/by-type?wallet=<wallet>&period=<YYYY-MM>
 * Returns { data: { wallet, period, totalPointsE18, byType: { daily_checkin, add_lp, trade, referral } }, success }.
 */
export const fetchPointsByType = async (wallet: string): Promise<GetPointsByTypeResponse> => {
  if (!POINTS_API_BASE) {
    throw new Error('Points API is not configured.')
  }

  const response = await fetch(getPointsByTypeEndpoint(wallet), {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return (await response.json()) as GetPointsByTypeResponse
}

/**
 * GET points/period-history?wallet=<address>&from=YYYY-MM&to=YYYY-MM&limit=N
 * Returns { data: { history: [{ period, startDate, endDate, totalPointsE18, byType: { daily_checkin, add_1p, trade, referral } }, ...] }, success }.
 * history length is up to limit.
 */
export const fetchPointsPeriodHistory = async (wallet: string): Promise<GetPointsPeriodHistoryResponse> => {
  if (!POINTS_API_BASE) {
    throw new Error('Points API is not configured.')
  }

  const response = await fetch(getPointsPeriodHistoryEndpoint(wallet), {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return (await response.json()) as GetPointsPeriodHistoryResponse
}

/**
 * GET points/checkin-history?wallet=<address>&period=<YYYY-MM>
 * Returns { data: { checkinHistory, serverTime, currentPeriod, periodTotalPointsE18, periodUserEarnedPointsE18, nextCheckinPointsE18, longestStreakThisPeriod }, success }.
 */
export const fetchCheckinHistory = async (wallet: string, period: string): Promise<GetCheckinHistoryResponse> => {
  if (!POINTS_API_BASE) {
    throw new Error('Points API is not configured.')
  }

  const response = await fetch(getCheckinHistoryEndpoint(wallet, period), {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return (await response.json()) as GetCheckinHistoryResponse
}

export type PostCheckinResponse = {
  data?: unknown
  success?: boolean
  [key: string]: unknown
}

/**
 * POST points/checkin – submit daily check-in. Body: { wallet, period }.
 */
export const postCheckin = async (wallet: string): Promise<PostCheckinResponse> => {
  if (!POINTS_API_BASE) {
    throw new Error('Points API is not configured.')
  }

  const url = joinUrl(POINTS_API_BASE, POINTS_CHECKIN_PATH)
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wallet }),
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return (await response.json()) as PostCheckinResponse
}
